import { BehaviorSubject, distinctUntilChanged, combineLatest, Subscription } from 'rxjs';
import * as i0 from '@angular/core';
import { EventEmitter, Directive, Output, Component, Input, HostListener, NgModule, Injectable } from '@angular/core';
import * as i1 from '@angular/forms';
import * as i2 from '@angular/router';
import * as i3 from '@angular/material/snack-bar';
import { MatSnackBarConfig } from '@angular/material/snack-bar';
import * as i1$1 from '@angular/common/http';
import { HttpParams, HttpClientModule } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { TranslocoModule, TRANSLOCO_CONFIG, translocoConfig, TRANSLOCO_LOADER } from '@ngneat/transloco';

class AbstractAvailableDataStateClass {
    constructor() {
        this.allData = new BehaviorSubject({
            availableData: new BehaviorSubject([]),
            isLoadingData: new BehaviorSubject(true),
            hasErrorLoadingData: new BehaviorSubject(false)
        });
    }
    get availableData() {
        return this.allData.value.availableData;
    }
    get isLoadingData() {
        return this.allData.value.isLoadingData;
    }
    get hasErrorLoadingData() {
        return this.allData.value.hasErrorLoadingData;
    }
    getSubscription(observable) {
        this.sendIsLoadingData(true);
        this.sendHasErrorLoadingData(false);
        return observable.subscribe({
            next: (res) => {
                this.sendAvailableData(res);
                this.sendHasErrorLoadingData(false);
                this.sendIsLoadingData(false);
            },
            error: () => {
                this.sendAvailableData([]);
                this.sendHasErrorLoadingData(true);
                this.sendIsLoadingData(false);
            }
        });
    }
    getSyncAllData() {
        return {
            availableData: this.availableData.getValue(),
            isLoadingData: this.isLoadingData.getValue(),
            hasErrorLoadingData: this.hasErrorLoadingData.getValue()
        };
    }
    getAsyncAllData() {
        return combineLatest({ availableData: this.availableData.pipe(distinctUntilChanged()), hasErrorLoadingData: this.hasErrorLoadingData.pipe(distinctUntilChanged()), isLoadingData: this.isLoadingData.pipe(distinctUntilChanged()) });
    }
    unsubscribe() {
        this.allData.value.availableData.unsubscribe();
        this.allData.value.isLoadingData.unsubscribe();
        this.allData.value.hasErrorLoadingData.unsubscribe();
        this.allData.unsubscribe();
    }
    resetAllStates() {
        this.sendAvailableData([]);
        this.sendIsLoadingData(false);
        this.sendHasErrorLoadingData(false);
    }
    sendAvailableData(availableData) {
        this.allData.value.availableData.next(availableData);
    }
    sendIsLoadingData(isLoading) {
        this.allData.value.isLoadingData.next(isLoading);
    }
    sendHasErrorLoadingData(hasErrorLoadingData) {
        this.allData.value.hasErrorLoadingData.next(hasErrorLoadingData);
    }
}

class AbstractHttpService {
    constructor(baseUrl) {
        this.baseUrl = baseUrl;
    }
}

class AbstractStateV2Service {
    constructor() {
        this.allData = new AbstractAvailableDataStateClass();
        this.selectedData = new BehaviorSubject(null);
        this.subscriptions = new Subscription();
    }
    getSyncAllData() {
        return this.allData.getSyncAllData();
    }
    getAsyncAllData() {
        return this.allData.getAsyncAllData();
    }
    getSyncAvailableData() {
        return this.allData.allData.value.availableData.getValue();
    }
    getAsyncAvailableData() {
        return this.allData.allData.value.availableData.asObservable();
    }
    getSyncIsLoadingData() {
        return this.allData.allData.value.isLoadingData.getValue();
    }
    getAsyncIsLoadingData() {
        return this.allData.allData.value.isLoadingData.asObservable();
    }
    getSyncHasErrorLoadingData() {
        return this.allData.allData.value.hasErrorLoadingData.getValue();
    }
    getASyncHasErrorLoadingData() {
        return this.allData.allData.value.hasErrorLoadingData.asObservable();
    }
    getSyncSelectedData() {
        return this.selectedData.getValue();
    }
    getAsyncSelectedData() {
        return this.selectedData.asObservable();
    }
    resetAllStates() {
        this.allData.resetAllStates();
        this.selectedData.next(null);
    }
}

class AbstractStateService {
    constructor() {
        this.isLoadingData = new BehaviorSubject(true);
        this.availableData = new BehaviorSubject([]);
        this.selectedData = new BehaviorSubject(null);
        this.subscriptions = new Subscription();
    }
    getSyncIsLoadingData() {
        return this.isLoadingData.getValue();
    }
    getAsyncIsLoadingData() {
        return this.isLoadingData.asObservable();
    }
    getSyncAvailableData() {
        return this.availableData.getValue();
    }
    getAsyncAvailableData() {
        return this.availableData.asObservable();
    }
    getSyncSelectedData() {
        return this.selectedData.getValue();
    }
    getAsyncSelectedData() {
        return this.selectedData.asObservable();
    }
    sendIsLoadingData(isLoading) {
        this.isLoadingData.next(isLoading);
    }
    sendAvailableData(availableData) {
        this.availableData.next(availableData);
    }
    sendSelectedData(selectedData) {
        this.selectedData.next(selectedData);
    }
}

// eslint-disable-next-line @typescript-eslint/ban-types
function getSubscription(observable, nextFunction, errorFunction, completeFunction) {
    return observable.subscribe({
        next: (res) => {
            nextFunction(res);
        },
        error: (err) => {
            if (errorFunction) {
                errorFunction(err);
            }
            else {
                console.log("Error!");
            }
        },
        complete: () => {
            if (completeFunction) {
                completeFunction();
            }
            else {
                console.log('Abgeschlossen');
            }
        },
    });
}

function setPageable(pageNumber, pageSize) {
    let params = new HttpParams();
    params = params.set('pageNumber', String(pageNumber));
    params = params.set('pageSize', String(pageSize));
    return params;
}
function setParams(paramValues) {
    let params = new HttpParams();
    paramValues.forEach((paramValue) => {
        if (paramValue) {
            params = params.set(Object.keys(paramValue)[0], String(paramValue));
        }
    });
    return params;
}
function setParamsFromObject(paramObject) {
    let params = new HttpParams();
    for (const filter in paramObject) {
        if (paramObject[filter] !== null && paramObject[filter] !== undefined && paramObject[filter] !== '') {
            params = params.set(filter, paramObject[filter]?.toString());
        }
    }
    return params;
}
function setPageableToParams(params, pageNumber = 0, pageSize = 500) {
    if (!(params.get('pageNumber') && params.get('pageSize'))) {
        params = params.set('pageNumber', String(pageNumber));
        params = params.set('pageSize', String(pageSize));
    }
    return params;
}

function getTimeHelper(passedDate) {
    const date = typeof passedDate === 'string' ? new Date(passedDate) : passedDate;
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
}

function convertUTCToLocalFast(dateString) {
    const date = new Date(dateString);
    const timezoneOffsetMin = date.getTimezoneOffset();
    date.setTime(date.getTime() - timezoneOffsetMin * 60 * 1000);
    return date.toISOString().slice(0, -1) +
        (timezoneOffsetMin > 0 ? '-' : '+') +
        (Math.abs(timezoneOffsetMin / 60) < 10 ? '0' : '') +
        Math.floor(Math.abs(timezoneOffsetMin / 60)) +
        ':' +
        (Math.abs(timezoneOffsetMin % 60) < 10 ? '0' : '') +
        Math.abs(timezoneOffsetMin % 60);
}

class GenericCreateFormComponent {
    constructor(fb, router, activatedRoute, matSnackbar) {
        this.fb = fb;
        this.router = router;
        this.activatedRoute = activatedRoute;
        this.matSnackbar = matSnackbar;
        this.successfulCreateEmitter = new EventEmitter();
        this.subscriptions = new Subscription();
        this.isInitialized = false;
        this.isWaitingForCreateResponse = false;
        this.hasFatalErrorOnInitialize = false;
        this.hasNonFatalErrorOnInitialize = false;
        this.createFailedNoConnection = false;
        this.createFailedInternalServer = false;
    }
    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }
    ngOnInit() {
        this.initializeFormGroup();
    }
    ngAfterContentInit() {
        this.initializeFormSubscriptions();
        this.initializeSubscriptions();
        this.isInitialized = true;
    }
    initializeFormGroup() {
        this.createFormGroup = this.createCreateFormGroup();
    }
    initializeFormSubscriptions() {
        this.initializeOnValueChangesSubscription();
    }
    initializeOnValueChangesSubscription() {
        this.subscriptions.add(this.createFormGroup.valueChanges.subscribe(res => {
            this.createFailedNoConnection = false;
            this.createFailedInternalServer = false;
            console.log(res);
        }));
    }
    initializeSubscriptions() {
        this.subscriptions.add(getSubscription(this.activatedRoute.params, this.onActivatedRouteChanges.bind(this)));
    }
    onActivatedRouteChanges(params) {
        this.routerParams = params;
    }
    onFormSubmitStart() {
        this.isWaitingForCreateResponse = true;
        this.createFailedNoConnection = false;
        this.createFailedInternalServer = false;
    }
    onFormSubmitSuccessful() {
        this.successfulCreateEmitter.emit(true);
        this.isWaitingForCreateResponse = false;
    }
    onFormSubmitError(error) {
        console.log(error);
        if (error.status === 0) {
            this.onConnectionLost();
        }
        if (error.status === 500) {
            this.onInternalServerError();
        }
        this.isWaitingForCreateResponse = false;
    }
    onConnectionLost() {
        this.createFailedNoConnection = true;
    }
    onInternalServerError() {
        this.createFailedInternalServer = true;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: GenericCreateFormComponent, deps: [{ token: i1.FormBuilder }, { token: i2.Router }, { token: i2.ActivatedRoute }, { token: i3.MatSnackBar }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.7", type: GenericCreateFormComponent, outputs: { successfulCreateEmitter: "successfulCreateEmitter" }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: GenericCreateFormComponent, decorators: [{
            type: Directive
        }], ctorParameters: () => [{ type: i1.FormBuilder }, { type: i2.Router }, { type: i2.ActivatedRoute }, { type: i3.MatSnackBar }], propDecorators: { successfulCreateEmitter: [{
                type: Output
            }] } });

class GenericEditFormComponent {
    constructor(fb, router, activatedRoute, matSnackbar) {
        this.fb = fb;
        this.router = router;
        this.activatedRoute = activatedRoute;
        this.matSnackbar = matSnackbar;
        this.successfulEditEmitter = new EventEmitter();
        this.subscriptions = new Subscription();
        this.isInitialized = false;
        this.hasFatalErrorOnInitialize = false;
        this.hasNonFatalErrorOnInitialize = false;
        this.isLoading = false;
        this.isWaitingForLoadingResponse = false;
        this.hasFailedLoadingNoConnection = false;
        this.hasFailedLoadingInternalServer = false;
        this.isWaitingForEditResponse = false;
        this.editFailedNoConnection = false;
        this.editFailedInternalServer = false;
    }
    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }
    ngOnInit() {
        this.initializeFormGroup();
    }
    ngAfterContentInit() {
        this.initializeFormSubscriptions();
        this.initializeSubscriptions();
        this.isInitialized = true;
    }
    initializeFormGroup() {
        this.editFormGroup = this.createEditFormGroup();
        this.editFormGroup.disable();
    }
    patchEditFormGroup(object) {
        this.editFormGroup.patchValue(object);
    }
    initializeFormSubscriptions() {
        this.initializeOnValueChangesSubscription();
    }
    initializeOnValueChangesSubscription() {
        this.subscriptions.add(this.editFormGroup.valueChanges.subscribe(res => {
            this.editFailedNoConnection = false;
            this.editFailedInternalServer = false;
            console.log(res);
        }));
    }
    initializeSubscriptions() {
        this.subscriptions.add(getSubscription(this.activatedRoute.params, this.onActivatedRouteChanges.bind(this)));
    }
    onActivatedRouteChanges(params) {
        this.routerParams = params;
    }
    onLoadDataStart() {
        this.isLoading = true;
        this.isWaitingForLoadingResponse = true;
        this.hasFailedLoadingInternalServer = false;
        this.hasFailedLoadingNoConnection = false;
    }
    onLoadDataSuccessful() {
        this.isLoading = false;
        this.isWaitingForLoadingResponse = false;
        this.editFormGroup.enable();
    }
    onLoadDataError(error) {
        console.log(error);
        if (error.status === 0) {
            this.onLoadObjectConnectionLost();
        }
        if (error.status === 500) {
            this.onLoadObjectInternalServer();
        }
        this.isLoading = false;
        this.isWaitingForLoadingResponse = false;
    }
    onLoadObjectConnectionLost() {
        this.hasFailedLoadingNoConnection = true;
    }
    onLoadObjectInternalServer() {
        this.hasFailedLoadingInternalServer = true;
    }
    onFormSubmitStart() {
        this.isWaitingForEditResponse = true;
        this.editFailedNoConnection = false;
        this.editFailedInternalServer = false;
    }
    onFormSubmitSuccessful() {
        this.successfulEditEmitter.emit(true);
        this.isWaitingForEditResponse = false;
    }
    onFormSubmitError(error) {
        console.log(error);
        if (error.status === 0) {
            this.onConnectionLost();
        }
        if (error.status === 500) {
            this.onInternalServerError();
        }
        this.isWaitingForEditResponse = false;
    }
    onConnectionLost() {
        this.editFailedNoConnection = true;
    }
    onInternalServerError() {
        this.editFailedInternalServer = true;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: GenericEditFormComponent, deps: [{ token: i1.FormBuilder }, { token: i2.Router }, { token: i2.ActivatedRoute }, { token: i3.MatSnackBar }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.7", type: GenericEditFormComponent, outputs: { successfulEditEmitter: "successfulEditEmitter" }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: GenericEditFormComponent, decorators: [{
            type: Directive
        }], ctorParameters: () => [{ type: i1.FormBuilder }, { type: i2.Router }, { type: i2.ActivatedRoute }, { type: i3.MatSnackBar }], propDecorators: { successfulEditEmitter: [{
                type: Output
            }] } });

class GenericDialogComponent {
    constructor() {
        this.isOpen = true;
        this.dialogClosedEmitter = new EventEmitter();
        this.dialogFinishedEmitter = new EventEmitter();
        this.dialogBackClickEmitter = new EventEmitter();
    }
    onClose() {
        console.log("onClose Triggered");
        this.dialogClosedEmitter.emit();
    }
    onFinish() {
        console.log("onFinish Triggered");
        this.dialogFinishedEmitter.emit();
    }
    onBackClick() {
        console.log("onBackClick Triggered");
        this.dialogBackClickEmitter.emit();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: GenericDialogComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.7", type: GenericDialogComponent, isStandalone: true, selector: "ng-component", inputs: { isOpen: "isOpen" }, outputs: { dialogClosedEmitter: "dialogClosedEmitter", dialogFinishedEmitter: "dialogFinishedEmitter", dialogBackClickEmitter: "dialogBackClickEmitter" }, ngImport: i0, template: '', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: GenericDialogComponent, decorators: [{
            type: Component,
            args: [{
                    template: '',
                    standalone: true
                }]
        }], propDecorators: { isOpen: [{
                type: Input
            }], dialogClosedEmitter: [{
                type: Output
            }], dialogFinishedEmitter: [{
                type: Output
            }], dialogBackClickEmitter: [{
                type: Output
            }] } });

class GenericOverviewDialogComponent extends GenericDialogComponent {
    constructor() {
        super(...arguments);
        this.level = 0;
    }
    setLevel(level) {
        this.level = level;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: GenericOverviewDialogComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.7", type: GenericOverviewDialogComponent, isStandalone: true, selector: "ng-component", usesInheritance: true, ngImport: i0, template: '', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: GenericOverviewDialogComponent, decorators: [{
            type: Component,
            args: [{
                    template: '',
                    standalone: true
                }]
        }] });

class ImageFallbackDirective {
    constructor() {
        this.fallbackSrc = '';
    }
    onProfileImageError(event) {
        const imgElement = event.target;
        if (this.fallbackSrc === '') {
            console.error('ImageFallback is missing the fallbackSrc!');
        }
        imgElement.src = this.fallbackSrc;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: ImageFallbackDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.7", type: ImageFallbackDirective, selector: "[elabImageFallback]", inputs: { fallbackSrc: "fallbackSrc" }, host: { listeners: { "error": "onProfileImageError($event)" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: ImageFallbackDirective, decorators: [{
            type: Directive,
            args: [{ selector: '[elabImageFallback]', }]
        }], propDecorators: { fallbackSrc: [{
                type: Input
            }], onProfileImageError: [{
                type: HostListener,
                args: ['error', ['$event']]
            }] } });

class ImageFallbackDirectiveModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: ImageFallbackDirectiveModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.7", ngImport: i0, type: ImageFallbackDirectiveModule, declarations: [ImageFallbackDirective], imports: [CommonModule], exports: [ImageFallbackDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: ImageFallbackDirectiveModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: ImageFallbackDirectiveModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [ImageFallbackDirective],
                    imports: [CommonModule],
                    exports: [ImageFallbackDirective],
                }]
        }] });

function isNotBlankValidator(control) {
    return control.value?.trim()?.length > 0 ? null : { 'isBlank': true };
}

function Match(controlName, matchingControlName) {
    // any is being used because UntypedFormGroup is not allowed with strict Angular/Typescript rules
    return (formGroup) => {
        const control = formGroup.controls[controlName];
        const matchingControl = formGroup.controls[matchingControlName];
        if (matchingControl.errors && !matchingControl.errors['mustMatch']) {
            return null;
        }
        if (control.value !== matchingControl.value) {
            matchingControl.setErrors({ 'mustMatch': true });
        }
        else {
            matchingControl.setErrors(null);
        }
        return null;
    };
}

function noWhitespaceValidator(control) {
    const hasWhitespace = (control.value.includes(' '));
    return !hasWhitespace ? null : { 'whitespace': true };
}

class TranslocoHttpLoader {
    constructor(http) {
        this.http = http;
    }
    getTranslation(lang) {
        return this.http.get(`/assets/i18n/${lang}.json`);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: TranslocoHttpLoader, deps: [{ token: i1$1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: TranslocoHttpLoader, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: TranslocoHttpLoader, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: i1$1.HttpClient }] });
class TranslocoRootModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: TranslocoRootModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.7", ngImport: i0, type: TranslocoRootModule, imports: [HttpClientModule], exports: [TranslocoModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: TranslocoRootModule, providers: [
            {
                provide: TRANSLOCO_CONFIG,
                useValue: translocoConfig({
                    availableLangs: ['de-DE', 'en-EN'],
                    defaultLang: 'de-DE',
                    reRenderOnLangChange: true,
                    prodMode: true
                })
            },
            { provide: TRANSLOCO_LOADER, useClass: TranslocoHttpLoader }
        ], imports: [HttpClientModule, TranslocoModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: TranslocoRootModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [HttpClientModule],
                    exports: [TranslocoModule],
                    providers: [
                        {
                            provide: TRANSLOCO_CONFIG,
                            useValue: translocoConfig({
                                availableLangs: ['de-DE', 'en-EN'],
                                defaultLang: 'de-DE',
                                reRenderOnLangChange: true,
                                prodMode: true
                            })
                        },
                        { provide: TRANSLOCO_LOADER, useClass: TranslocoHttpLoader }
                    ]
                }]
        }] });

class CustomMatSnackbarService {
    constructor(snackBar) {
        this.snackBar = snackBar;
    }
    openInfo(message) {
        const config = new MatSnackBarConfig();
        config.panelClass = ['custom-snackbar', 'info-light'];
        config.duration = 4000;
        this.snackBar.open(message, undefined, config);
    }
    openSuccess(message) {
        const config = new MatSnackBarConfig();
        config.panelClass = ['custom-snackbar', 'success-light'];
        config.duration = 4000;
        this.snackBar.open(message, undefined, config);
    }
    openError(message) {
        const config = new MatSnackBarConfig();
        config.panelClass = ['custom-snackbar', 'error-light'];
        config.duration = 4000;
        this.snackBar.open(message, undefined, config);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: CustomMatSnackbarService, deps: [{ token: i3.MatSnackBar }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: CustomMatSnackbarService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: CustomMatSnackbarService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: i3.MatSnackBar }] });

/**
 * Generated bundle index. Do not edit.
 */

export { AbstractAvailableDataStateClass, AbstractHttpService, AbstractStateService, AbstractStateV2Service, CustomMatSnackbarService, GenericCreateFormComponent, GenericDialogComponent, GenericEditFormComponent, GenericOverviewDialogComponent, ImageFallbackDirective, ImageFallbackDirectiveModule, Match, TranslocoHttpLoader, TranslocoRootModule, convertUTCToLocalFast, getSubscription, getTimeHelper, isNotBlankValidator, noWhitespaceValidator, setPageable, setPageableToParams, setParams, setParamsFromObject };
